/**
 * Analytics Portal V1 — the Analytics route (Phase 9).
 *
 * Responsibilities: gate the whole surface on the server's safe config, own the URL state,
 * fetch the managed entities and the active dashboard, and hand everything to the shell. When
 * the portal is disabled this component renders the **existing** legacy analytics page and
 * issues no dashboard, entity or refresh request at all.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import LegacyAnalytics from "@/pages/app/Analytics";
import { AnalyticsShell } from "@/components/analytics/AnalyticsShell";
import { EmptyState } from "@/components/analytics/EmptyState";
import { ErrorState } from "@/components/analytics/ErrorState";
import { LoadingSkeleton } from "@/components/analytics/LoadingSkeleton";
import { LockedOverlay } from "@/components/analytics/LockedOverlay";
import { useAnalyticsConfig, useAnalyticsDashboard, useManagedEntities } from "@/hooks/useAnalyticsData";
import { useAnalyticsUrlState } from "@/hooks/useAnalyticsUrlState";
import { COMING_SOON_TABS, TAB_ENTITY_TYPE, normalizeAnalyticsError, refreshAnalytics } from "@/lib/analyticsApi";
import SummaryTab from "./SummaryTab";
import ProfileTab from "./ProfileTab";
import BusinessTab from "./BusinessTab";
import CapitalTab from "./CapitalTab";
import EventsTab from "./EventsTab";
import ResourcesTab from "./ResourcesTab";

const PortalContext = createContext(null);
export const useAnalyticsPortal = () => useContext(PortalContext);

export default function AnalyticsPortal() {
  const { config, loading, portalActive, locked, legacyEnabled } = useAnalyticsConfig();
  const url = useAnalyticsUrlState({
    presets: config?.presets || [],
    defaultPreset: config?.default_preset || "last_30_days",
  });
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const entityType = TAB_ENTITY_TYPE[url.tab] || null;
  const comingSoon = COMING_SOON_TABS.includes(url.tab);
  const entitiesEnabled = portalActive && !locked && !comingSoon;
  const entitiesQuery = useManagedEntities({
    category: url.tab === "summary" ? url.category : entityType,
    enabled: entitiesEnabled,
  });
  // Scope defensively to the tab's entity type: while the category-specific list is still
  // loading the cache may hold another tab's list, and an entity of the wrong type would be a
  // guaranteed 403. The server re-authorizes regardless — this just keeps the UI honest.
  const allEntities = entitiesQuery.data?.entities || [];
  const entities = entityType
    ? allEntities.filter((row) => row.entity_type === entityType)
    : allEntities;

  // Safe fallback: an unknown or unauthorized entity id in the URL resolves to the first
  // entity the server says this viewer may open, and never to a request we know will 403.
  const known = entities.some((row) => row.entity_id === url.entity);
  useEffect(() => {
    if (comingSoon) {
      if (url.entity) url.setEntity(null, { replace: true });   // no entity applies yet
      return;
    }
    if (url.tab === "summary" || entitiesQuery.isLoading) return;
    if (!entities.length) {
      if (url.entity) url.setEntity(null, { replace: true });
      return;
    }
    if (!known) url.setEntity(entities[0].entity_id, { replace: true });
  }, [url.tab, url.entity, known, entities, entitiesQuery.isLoading]); // eslint-disable-line

  const dashboard = useAnalyticsDashboard({
    tab: url.tab,
    entityId: known ? url.entity : null,
    category: url.category,
    range: url.range,
    enabled: portalActive && !locked && !comingSoon,
  });

  const payload = dashboard.data && dashboard.data.locked !== true ? dashboard.data : null;
  const error = dashboard.error ? normalizeAnalyticsError(dashboard.error) : null;

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      if (url.tab !== "summary" && known && url.entity) {
        await refreshAnalytics({ tab: url.tab, entityId: url.entity });
      }
      await queryClient.invalidateQueries({ queryKey: ["analytics", "dashboard"] });
    } catch {
      /* a failed refresh must never break the page; the payload on screen stays valid */
    } finally {
      setRefreshing(false);
    }
  };

  const contextValue = {
    config, payload, tab: url.tab, entity: url.entity, entities, range: url.range,
    loading: dashboard.isLoading || dashboard.isFetching,
    error, refetch: dashboard.refetch,
    onUseSupportedRange: (suggestion) => url.setCustomRange(suggestion.start, suggestion.end),
    onOpenEntity: (tab, entityId) => url.goToTab(tab, { entity: entityId }),
    onChangeRange: () => url.setPreset("last_year"),
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8">
        <LoadingSkeleton />
      </div>
    );
  }

  // Portal disabled (or admin-only for a non-admin): the app looks exactly as it did before.
  if (!portalActive) {
    return legacyEnabled ? <LegacyAnalytics /> : (
      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">
        <EmptyState state="service-unavailable" />
      </div>
    );
  }

  const body = locked ? (
    <LockedOverlay lockCopy={config?.lock_copy} tabs={config?.tabs || []} />
  ) : error ? (
    <ErrorState error={error} onRetry={dashboard.refetch} />
  ) : dashboard.isLoading ? (
    <LoadingSkeleton />
  ) : (
    <Routes>
      <Route index element={<SummaryTab />} />
      <Route path="profile" element={<ProfileTab />} />
      <Route path="business" element={<BusinessTab />} />
      <Route path="capital" element={<CapitalTab />} />
      <Route path="events" element={<EventsTab />} />
      <Route path="resources" element={<ResourcesTab />} />
      <Route path="*" element={<SummaryTab />} />
    </Routes>
  );

  return (
    <PortalContext.Provider value={contextValue}>
      <AnalyticsShell
        config={config} tab={url.tab} tabs={config?.tabs || []} onTabChange={url.goToTab}
        entities={entities} entitiesLoading={entitiesQuery.isLoading} entity={url.entity}
        showEntitySelector={!comingSoon} showFilters={!comingSoon}
        onEntityChange={url.setEntity} category={url.category} onCategoryChange={url.setCategory}
        counts={entitiesQuery.data?.counts_by_type} range={url.range}
        rangeLabel={payload?.reporting?.range_label}
        comparisonLabel={payload?.reporting?.comparison_label}
        onPreset={url.setPreset} onCustomRange={url.setCustomRange}
        onCompare={url.setCompare} onRefresh={onRefresh} refreshing={refreshing}
        freshness={payload?.freshness} payload={payload}
      >
        {body}
      </AnalyticsShell>
    </PortalContext.Provider>
  );
}
